/**
 * OI Flow Charts page (Option Indicator Phase B — "OI Flow Charts v2" rebuild,
 * matched against the original standalone app's `/charts` page reference
 * screenshot). Talks to the same /api/v1/oi-charts/* endpoint and the same
 * `ChartHistoryPoint` schema as before — no backend changes were needed for
 * this rebuild: `fresh_pcr` (Fresh Put Ratio), `nr` (Net Ratio), `vwoi_gap`
 * (Near-Wide VWOI Gap), `bull_streak`/`bear_streak`, `mp1`, `fp`/`fc`, and
 * `fp_delta`/`fc_delta` were already computed and persisted by
 * `app.services.option_indicator.chart_history` (see that module's
 * docstring for the full field-by-field origin and disclosed gaps).
 *
 * Layout changes from the previous version: NIFTY and BANKNIFTY are now
 * shown side by side (matching the reference), each with its own header
 * (spot, MP1, snap count), a badge row (FP / FC / FP Δ / NR / NW GAP /
 * STREAK), and three stacked chart panels (Fresh Put/Call + Spot, Net
 * Ratio + Fresh PCR, Near-Wide VWOI Gap + FP Δ/snap). A 6-option timeframe
 * selector (10/15/30/60/90 min, Full day) slices the already-fetched
 * full-day series client-side, the same pattern used on the Strike Charts
 * tab — no new endpoint or query param needed.
 *
 * The "Not Yet Available" disclosure panel from the previous version is
 * preserved unchanged: those original-app fields are still not persisted
 * anywhere in TekInvest, so they are still honestly disclosed rather than
 * fabricated or silently dropped.
 */
import { indexSectionProps, useAnalystIndexView } from "@/context/AnalystIndexViewContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import oiChartsService from "@/services/oiChartsService";
import indiaVixService from "@/services/indiaVixService";
import type { ChartHistoryPoint, ChartHistoryResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, ChartTooltip, Legend);

const HISTORY_LIMIT = 200;

function isNotFoundError(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === "object" &&
      "response" in err &&
      (err as { response?: { status?: number } }).response?.status === 404,
  );
}

// Same IST-today / banner-date helpers already established on the Strike
// Charts tab (Option Indicator Phase C weekend fallback) for its historical
// date picker -- no shared date-util module exists in this codebase yet, so
// this is the same small local duplicate.
const IST_OFFSET_MINUTES = 5 * 60 + 30;
function todayIsoInIST(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MINUTES * 60_000 + now.getTimezoneOffset() * 60_000);
  return ist.toISOString().slice(0, 10);
}
function formatBannerDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

interface TimeframeOption {
  label: string;
  minutes: number | null;
}
const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { label: "10 min", minutes: 10 },
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "60 min", minutes: 60 },
  { label: "90 min", minutes: 90 },
  { label: "Full day", minutes: null },
];
const DEFAULT_TIMEFRAME_LABEL = "Full day";

function parseTsToMinutes(ts: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(ts);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

// Walks backward from the newest point and stops at the first place the
// clock-time goes backwards (i.e. wraps from the previous trading day into
// the latest one). HISTORY_LIMIT (200) can span more than one day's worth
// of 5-minute bars, so without this, "Full day" would silently include
// prior sessions too.
function latestSessionStart(points: ChartHistoryPoint[]): number {
  if (points.length === 0) return 0;
  let startIdx = points.length - 1;
  for (let i = points.length - 1; i > 0; i--) {
    const cur = parseTsToMinutes(points[i].ts);
    const prev = parseTsToMinutes(points[i - 1].ts);
    if (cur === null || prev === null) break;
    if (prev > cur) {
      startIdx = i;
      break;
    }
    startIdx = i - 1;
  }
  return startIdx;
}

// Slices `points` down to the trailing `minutes` window, measured from the
// series' own latest bar (not wall-clock now) -- same convention as the
// Strike Charts tab's timeframe filter. `null` (Full day) returns the
// latest trading session only (see `latestSessionStart`), not every
// fetched snapshot.
function filterByTimeframe(points: ChartHistoryPoint[], minutes: number | null): ChartHistoryPoint[] {
  if (points.length === 0) return points;
  const sessionPoints = points.slice(latestSessionStart(points));
  if (minutes === null) return sessionPoints;
  const lastMinutes = parseTsToMinutes(sessionPoints[sessionPoints.length - 1].ts);
  if (lastMinutes === null) return sessionPoints;
  return sessionPoints.filter((p) => {
    const t = parseTsToMinutes(p.ts);
    return t !== null && lastMinutes - t >= 0 && lastMinutes - t <= minutes;
  });
}

const DISCLOSED_GAPS: { label: string; note: string }[] = [
  {
    label: "Unwind Zone (Strategy 2)",
    note: "ATM-zone call/put unwind totals are computed live today but never persisted.",
  },
  {
    label: "Absorption / Floor & Ceiling Forming (Strategy 3)",
    note: "Absorption ratios are computed live but never persisted; floor/ceiling-forming detection does not exist in this codebase yet.",
  },
  {
    label: "ATM Momentum (Strategy 6)",
    note: "Per-strike ATM LTP/volume and momentum streaks have no persisted source.",
  },
  {
    label: "Acceleration Signal & Expected Move",
    note: "Both are computed live upstream today but never persisted as history.",
  },
  {
    label: "Near/Wide VWOI split",
    note: "Only the combined Near-Wide VWOI Gap is persisted; the separate near-band and wide-band values are computed live but never stored.",
  },
];

function fmtL(n: number | null | undefined): string {
  if (n === null || n === undefined) return "N/A";
  return `${n.toFixed(1)}L`;
}
function fmt3(n: number | null | undefined): string {
  if (n === null || n === undefined) return "N/A";
  return n.toFixed(3);
}
function colorForSign(n: number | null | undefined): string {
  if (n === null || n === undefined) return "text.secondary";
  if (n > 0) return "#3fb950";
  if (n < 0) return "#f85149";
  return "text.secondary";
}

// Fresh Put/Fresh Call are large raw quantities. The panel's left axis is
// titled "Lakhs", but until now the ticks and tooltip just printed the
// raw number with Western thousand-separators -- never actually divided
// by 1,00,000 or grouped the Indian way. Both now do: a tick reading
// "800.0" under the "Lakhs" title really does mean 8,00,00,000 raw
// units, and hovering shows the same converted value with an explicit
// "L" suffix.
function toLakhs(value: number): number {
  return value / 100_000;
}
function formatLakhTick(value: number | string): string {
  const num = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(num)) return String(value);
  return toLakhs(num).toLocaleString("en-IN", { maximumFractionDigits: 1 });
}
function formatLakhTooltip(value: number): string {
  return `${toLakhs(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}L`;
}

const PANEL_HEIGHT_1 = 220;
const PANEL_HEIGHT_2 = 150;
const PANEL_HEIGHT_3 = 150;

interface OIFlowIndexPanelProps {
  label: string;
  fullPoints: ChartHistoryPoint[];
  timeframeMinutes: number | null;
}

function OIFlowIndexPanel({ label, fullPoints, timeframeMinutes }: OIFlowIndexPanelProps) {
  const windowPoints = useMemo(() => filterByTimeframe(fullPoints, timeframeMinutes), [fullPoints, timeframeMinutes]);
  const latest = fullPoints.length > 0 ? fullPoints[fullPoints.length - 1] : null;

  if (!latest) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
          <Typography variant="caption" color="text.secondary">
            No scored history for {label} yet.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const labels = windowPoints.map((p) => p.ts);
  const streakActive = latest.bull_streak > 0 || latest.bear_streak > 0;
  const streakText = latest.bull_streak > 0 ? `🟢${latest.bull_streak}` : latest.bear_streak > 0 ? `🔴${latest.bear_streak}` : "—";
  const streakColor = latest.bull_streak > 0 ? "#3fb950" : latest.bear_streak > 0 ? "#f85149" : "text.secondary";

  const badges: Array<[string, string, string]> = [
    ["FP", fmtL(latest.fp), colorForSign(latest.fp)],
    ["FC", fmtL(latest.fc), colorForSign(latest.fc)],
    ["FP Δ", fmtL(latest.fp_delta), colorForSign(latest.fp_delta)],
    ["NR", fmt3(latest.nr), colorForSign(latest.nr)],
    ["NW GAP", fmt3(latest.vwoi_gap), colorForSign(latest.vwoi_gap)],
    ["STREAK", streakText, streakColor],
  ];

  const chartOptionsBase = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: { position: "bottom" as const, labels: { boxWidth: 10, font: { size: 9 } } },
    },
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fullPoints.length} snaps · {latest.ts}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ fontFamily: "monospace" }}>
          {latest.spot.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          MP1 {latest.mp1 != null ? latest.mp1.toLocaleString("en-IN") : "—"}
        </Typography>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "1px", mb: 1.5 }}>
        {badges.map(([lbl, val, color]) => (
          <Box key={lbl} sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", fontSize: 9, textTransform: "uppercase" }}
            >
              {lbl}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color }}>
              {val}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        FRESH PUT (GREEN) · FRESH CALL (RED) · SPOT (DASHED)
      </Typography>
      <Box sx={{ height: PANEL_HEIGHT_1, mb: 2 }}>
        <Chart
          type="line"
          options={{
            ...chartOptionsBase,
            plugins: {
              ...chartOptionsBase.plugins,
              tooltip: {
                callbacks: {
                  label: (ctx: { dataset: { label?: string; yAxisID?: string }; parsed: { y: number | null } }) => {
                    const raw = ctx.parsed.y;
                    const label = ctx.dataset.label ?? "";
                    if (raw === null || raw === undefined) return label;
                    if (ctx.dataset.yAxisID === "y") {
                      return `${label}: ${formatLakhTooltip(raw)}`;
                    }
                    return `${label}: ${raw.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
                  },
                },
              },
            },
            scales: {
              x: { ticks: { maxTicksLimit: 8, font: { size: 9 } } },
              y: {
                position: "left" as const,
                ticks: { font: { size: 9 }, callback: (value: number | string) => formatLakhTick(value) },
                title: { display: true, text: "Lakhs", font: { size: 9 } },
              },
              y1: {
                position: "right" as const,
                grid: { drawOnChartArea: false },
                ticks: { font: { size: 9 } },
              },
            },
          }}
          data={{
            labels,
            datasets: [
              {
                type: "line" as const,
                label: "Fresh Put (L)",
                data: windowPoints.map((p) => p.fp),
                borderColor: "#3fb950",
                backgroundColor: "#3fb950",
                pointRadius: 2,
                yAxisID: "y",
              },
              {
                type: "line" as const,
                label: "Fresh Call (L)",
                data: windowPoints.map((p) => p.fc),
                borderColor: "#f85149",
                backgroundColor: "#f85149",
                pointRadius: 2,
                yAxisID: "y",
              },
              {
                type: "line" as const,
                label: "Spot",
                data: windowPoints.map((p) => p.spot),
                borderColor: "rgba(160,160,160,0.7)",
                backgroundColor: "rgba(160,160,160,0.7)",
                borderDash: [4, 4],
                pointRadius: 0,
                yAxisID: "y1",
              },
            ],
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        NET RATIO (COLOURED ±0.35) · FRESH PCR (BLUE)
      </Typography>
      <Box sx={{ height: PANEL_HEIGHT_2, mb: 2 }}>
        <Chart
          type="line"
          options={{
            ...chartOptionsBase,
            scales: {
              x: { ticks: { maxTicksLimit: 8, font: { size: 9 } } },
              y: { position: "left" as const, ticks: { font: { size: 9 } }, min: -1, max: 1 },
              y1: {
                position: "right" as const,
                grid: { drawOnChartArea: false },
                ticks: { font: { size: 9 } },
              },
            },
          }}
          data={{
            labels,
            datasets: [
              {
                type: "line" as const,
                label: "Net Ratio",
                data: windowPoints.map((p) => p.nr),
                borderWidth: 2,
                pointRadius: 0,
                yAxisID: "y",
                segment: {
                  borderColor: (ctx: { p0: { parsed: { y: number | null } }; p1: { parsed: { y: number | null } } }) => {
                    const y0 = ctx.p0.parsed.y;
                    const y1 = ctx.p1.parsed.y;
                    if (y0 === null || y1 === null) return "rgba(150,150,150,0.85)";
                    const v = (y0 + y1) / 2;
                    if (v > 0.35) return "#3fb950";
                    if (v < -0.35) return "#f85149";
                    return "rgba(150,150,150,0.85)";
                  },
                },
              },
              {
                type: "line" as const,
                label: "Fresh PCR",
                data: windowPoints.map((p) => p.fresh_pcr),
                borderColor: "#4dabf7",
                backgroundColor: "#4dabf7",
                pointRadius: 0,
                borderWidth: 1.5,
                yAxisID: "y1",
              },
            ],
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        NEAR-WIDE VWOI GAP · FP Δ/SNAP (PUT WALL EROSION)
      </Typography>
      <Box sx={{ height: PANEL_HEIGHT_3 }}>
        <Chart
          type="bar"
          options={{
            ...chartOptionsBase,
            scales: {
              x: { ticks: { maxTicksLimit: 8, font: { size: 9 } } },
              y: { position: "left" as const, ticks: { font: { size: 9 } } },
              y1: {
                position: "right" as const,
                grid: { drawOnChartArea: false },
                ticks: { font: { size: 9 } },
              },
            },
          }}
          data={{
            labels,
            datasets: [
              {
                type: "bar" as const,
                label: "NW VWOI Gap",
                data: windowPoints.map((p) => p.vwoi_gap),
                backgroundColor: windowPoints.map((p) =>
                  (p.vwoi_gap ?? 0) >= 0 ? "rgba(255,169,77,0.8)" : "rgba(248,81,73,0.75)",
                ),
                yAxisID: "y",
              },
              {
                type: "line" as const,
                label: "FP Δ/snap",
                data: windowPoints.map((p) => p.fp_delta),
                borderColor: "#4dabf7",
                backgroundColor: "#4dabf7",
                pointRadius: 0,
                borderWidth: 1.5,
                yAxisID: "y1",
              },
            ],
          }}
        />
      </Box>

      {streakActive && (
        <Chip
          size="small"
          sx={{ mt: 1.5 }}
          label={
            latest.bull_streak > 0
              ? `Composite bull streak: ${latest.bull_streak}`
              : `Composite bear streak: ${latest.bear_streak}`
          }
          color={latest.bull_streak > 0 ? "success" : "error"}
        />
      )}
    </Paper>
  );
}

export function OIChartsPage() {
  const view = useAnalystIndexView();
  const [niftyData, setNiftyData] = useState<ChartHistoryResponse | null>(null);
  const [bnData, setBnData] = useState<ChartHistoryResponse | null>(null);
  const [vixLtp, setVixLtp] = useState<number | null>(null);
  const [vixRegime, setVixRegime] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [timeframeLabel, setTimeframeLabel] = useState<string>(DEFAULT_TIMEFRAME_LABEL);
  // Option Indicator Phase D (OI Flow historical mode) -- null means "live",
  // matching the same convention the Strike Charts tab already uses.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const maxDate = useMemo(() => todayIsoInIST(), []);

  const niftyPoints = niftyData?.points ?? [];
  const bnPoints = bnData?.points ?? [];

  const timeframeMinutes = useMemo(
    () => TIMEFRAME_OPTIONS.find((opt) => opt.label === timeframeLabel)?.minutes ?? null,
    [timeframeLabel],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [nfResult, bnResult, vixResult] = await Promise.allSettled([
      oiChartsService.getHistory("NIFTY", HISTORY_LIMIT, selectedDate ?? undefined),
      oiChartsService.getHistory("BANKNIFTY", HISTORY_LIMIT, selectedDate ?? undefined),
      indiaVixService.getCurrent(),
    ]);

    if (nfResult.status === "fulfilled") {
      setNiftyData(nfResult.value);
    } else if (!isNotFoundError(nfResult.reason)) {
      setError(extractErrorMessage(nfResult.reason));
    }

    if (bnResult.status === "fulfilled") {
      setBnData(bnResult.value);
    } else if (!isNotFoundError(bnResult.reason)) {
      setError((prev) => prev ?? extractErrorMessage(bnResult.reason));
    }

    if (vixResult.status === "fulfilled") {
      setVixLtp(vixResult.value.ltp);
      setVixRegime(vixResult.value.vix_regime ?? null);
    }

    setLastLoadedAt(new Date());
    setLoading(false);
  }, [selectedDate]);

  useEffect(() => {
    load();
    // A historical date is a frozen snapshot -- no live scheduler will ever
    // write new bars for it, so the 5-minute auto-refresh only runs for the
    // live (selectedDate === null) view, same convention as Strike Charts.
    if (selectedDate !== null) return;
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load, selectedDate]);

  // While either index is "backfilling" (no data existed yet for the
  // selected historical date, so a job was just started/reused), poll its
  // progress and automatically re-fetch once it's no longer in flight --
  // the same job may finish with real data or with a genuinely-empty result
  // (never fabricated), either way `load()` re-reads the honest outcome.
  useEffect(() => {
    const jobIds = [niftyData?.backfill_job_id, bnData?.backfill_job_id].filter(
      (id): id is string => Boolean(id),
    );
    if (jobIds.length === 0) return;

    let cancelled = false;
    const TERMINAL = ["COMPLETED", "FAILED", "CANCELLED"];
    const poll = async () => {
      const results = await Promise.allSettled(jobIds.map((id) => oiChartsService.getBackfillStatus(id)));
      if (cancelled) return;
      const stillRunning = results.some(
        (r) => r.status === "fulfilled" && !TERMINAL.includes(r.value.job.status),
      );
      if (!stillRunning) {
        load();
      }
    };
    const id = window.setInterval(poll, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [niftyData?.backfill_job_id, bnData?.backfill_job_id, load]);

  const hasAnyData = niftyPoints.length > 0 || bnPoints.length > 0;
  const backfillingLabel =
    niftyData?.data_status === "backfilling"
      ? "NIFTY"
      : bnData?.data_status === "backfilling"
        ? "BANKNIFTY"
        : null;
  const unavailableForSelectedDate =
    selectedDate !== null && (niftyData?.data_status === "unavailable" || bnData?.data_status === "unavailable");

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" fontWeight={700}>
            OI Flow Charts
          </Typography>
          {vixLtp !== null && (
            <Chip
              size="small"
              label={`VIX ${vixLtp.toFixed(1)}${vixRegime ? ` ${vixRegime}` : ""}`}
              sx={{ fontFamily: "monospace" }}
            />
          )}
        </Stack>
        {lastLoadedAt && (
          <Typography variant="caption" color="text.secondary">
            Last refreshed: {lastLoadedAt.toLocaleTimeString()}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={timeframeLabel}
          exclusive
          size="small"
          onChange={(_event, value) => {
            if (value) setTimeframeLabel(value);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontFamily: "monospace",
              fontSize: 12,
              px: 1.5,
              py: 0.25,
              color: "text.secondary",
              borderColor: "divider",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              color: "#00d68f",
              backgroundColor: "rgba(0,214,143,0.12)",
              borderColor: "#00d68f",
            },
          }}
        >
          {TIMEFRAME_OPTIONS.map((opt) => (
            <ToggleButton key={opt.label} value={opt.label}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          type="date"
          size="small"
          label="Historical session"
          value={selectedDate ?? ""}
          onChange={(event) => setSelectedDate(event.target.value || null)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxDate }}
          sx={{ width: 190 }}
        />

        {selectedDate !== null && (
          <Chip
            label={`Viewing ${formatBannerDate(selectedDate)}`}
            size="small"
            onDelete={() => setSelectedDate(null)}
          />
        )}
      </Stack>

      {backfillingLabel && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No OI Flow data found in the database for {selectedDate ? formatBannerDate(selectedDate) : ""} yet --
          fetching it from the historical option-chain source now (started with {backfillingLabel}). This can
          take a few minutes; this page will refresh automatically once it's ready.
        </Alert>
      )}

      {unavailableForSelectedDate && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {selectedDate ? formatBannerDate(selectedDate) : "The selected date"} is a weekend or a future date --
          no OI Flow data can ever exist for it.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Fresh put/call OI flow, Net Ratio, Fresh PCR (Fresh Put Ratio) and Near-Wide VWOI Gap, replayed
        from TekInvest's persisted OI score history -- the same fields the Option Indicator page
        scores from. See the disclosure panel below for the handful of original-app fields with no
        persisted history yet.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !hasAnyData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !hasAnyData ? (
        selectedDate === null ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No scored history yet. Run a collect+score cycle (Option Indicator page) and check back.
          </Alert>
        ) : null
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "md")}>
            <OIFlowIndexPanel label="NIFTY" fullPoints={niftyPoints} timeframeMinutes={timeframeMinutes} />
          </Grid>
          <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "md")}>
            <OIFlowIndexPanel label="BANKNIFTY" fullPoints={bnPoints} timeframeMinutes={timeframeMinutes} />
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Not Yet Available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          These fields exist in the original standalone app but are computed live upstream and never
          persisted, so there is no history to replay yet. They will appear here once a future
          migration starts capturing them going forward (no historical backfill will be possible for
          the period before that migration ships).
        </Typography>
        <Stack spacing={0.5}>
          {DISCLOSED_GAPS.map((gap) => (
            <Typography key={gap.label} variant="body2">
              <strong>{gap.label}:</strong> {gap.note}
            </Typography>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

export default OIChartsPage;
